import { stripLocalePrefix } from '../utils/historyEntry';
import { toNavLocation } from '../utils/navPath';

type RouteEvent = 'routeChangeStart' | 'routeChangeError';
/** Next 라우터 이벤트 인자 — routeChangeStart(url), routeChangeError(error, url). */
type RouteEventHandler = (...args: unknown[]) => void;

export type FreshArrivalRouter = {
  events: {
    on(event: RouteEvent, handler: RouteEventHandler): void;
    off(event: RouteEvent, handler: RouteEventHandler): void;
  };
  locales?: readonly string[];
};

/**
 * 이전 길을 잇지 않고 새로 시작할 도착 주소(쿼리 없이) → 예약 해제 함수.
 * 그 주소가 기록되면 소비하고, 다른 곳으로 가거나 그 주소로 가는 이동이 실패하면 해제한다.
 */
const reservations = new Map<string, () => void>();

/** `routeChangeStart` 주소에 붙는 로케일 접두사(`/ja/...`)를 비교 전에 뗀다. */
function toRouteLocation(url: string, locales: readonly string[] = []) {
  return toNavLocation(stripLocalePrefix(url, locales));
}

/** 그 주소로 가는 다음 도착 하나를 「새 길」로 예약한다. 이동 직전에 부른다. */
export function reserveFreshArrival(
  router: FreshArrivalRouter,
  href: string,
): () => void {
  const location = toNavLocation(href);
  reservations.get(location)?.();

  const isTarget = (url: unknown) =>
    typeof url === 'string' &&
    toRouteLocation(url, router.locales) === location;

  function onRouteChangeStart(...[url]: unknown[]) {
    if (typeof url === 'string' && !isTarget(url)) release();
  }
  // 새 이동을 시작하면 Next 는 진행 중이던 이전 이동을 취소하며 그 이동의 routeChangeError 를 먼저 보낸다.
  // 이 예약의 주소로 가는 이동이 실패했을 때만 푼다.
  function onRouteChangeError(...[, url]: unknown[]) {
    if (isTarget(url)) release();
  }
  function release() {
    router.events.off('routeChangeStart', onRouteChangeStart);
    router.events.off('routeChangeError', onRouteChangeError);
    if (reservations.get(location) === release) reservations.delete(location);
  }

  router.events.on('routeChangeStart', onRouteChangeStart);
  router.events.on('routeChangeError', onRouteChangeError);
  reservations.set(location, release);
  return release;
}

export const hasFreshArrival = (location: string) => reservations.has(location);

/** 그 주소가 기록될 때 예약을 소비한다. 예약이 있었으면 true. */
export function consumeFreshArrival(location: string): boolean {
  const release = reservations.get(location);
  release?.();
  return release !== undefined;
}
